import { useState } from "react";
import { useTranslations } from "next-intl";
import { Button, Heading, Text } from "@/components/ui";
import { apiErrorText, readApiError } from "@/lib/api-error";
import { saveJoinPolicy, type JoinPolicy, type JoinSettings } from "@/services/api/allowlist";

type Props = {
  settings: JoinSettings;
  onChanged: (settings: JoinSettings) => void;
};

/**
 * §31 "Who can join": Anyone / Only approved emails. Switching to approved-only says first how
 * many current students go on the list (the server adds them in the same step) and, S3 F-3, how
 * many are unverified (signed out and asked for an emailed code, F-13). With no email sender the
 * server refuses (`EMAIL_NOT_CONFIGURED`); a second confirmation switches anyway (`force`).
 */
export function JoinPolicySetting({ settings, onChanged }: Props) {
  const t = useTranslations("allowlist");
  const tRoot = useTranslations();
  const [choice, setChoice] = useState<JoinPolicy>(settings.policy);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (choice === settings.policy) return;
    const unverified = settings.unverifiedStudentCount ?? 0;
    const question =
      choice === "approved_only"
        ? [
            t("confirmApprovedOnly", { count: settings.unlistedStudentCount }),
            unverified > 0 ? t("confirmApprovedOnlyUnverified", { count: unverified }) : "",
          ]
            .filter(Boolean)
            .join(" ")
        : t("confirmOpen");
    if (!window.confirm(question)) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const next = await saveJoinPolicy(choice).catch((err: unknown) => {
        if (
          choice === "approved_only" &&
          readApiError(err)?.code === "EMAIL_NOT_CONFIGURED" &&
          window.confirm(t("confirmForceNoEmail"))
        ) {
          return saveJoinPolicy(choice, { force: true });
        }
        throw err;
      });
      onChanged(next);
      setNotice(
        next.policy === "approved_only"
          ? t("switchedApprovedOnly", { count: next.addedStudents })
          : t("switchedOpen"),
      );
    } catch (err) {
      setError(
        apiErrorText(err, tRoot, "allowlist.policyFailed", { namespace: "allowlist.errors" }),
      );
    } finally {
      setBusy(false);
    }
  }

  const option = (value: JoinPolicy, label: string, hint: string) => (
    <label className="flex cursor-pointer items-start gap-3 rounded-md border border-[var(--border)] p-3">
      <input
        type="radio"
        name="join-policy"
        value={value}
        checked={choice === value}
        onChange={() => setChoice(value)}
        className="mt-1"
        data-testid={`join-policy-${value}`}
      />
      <span>
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-xs text-[var(--text-muted)]">{hint}</span>
      </span>
    </label>
  );

  return (
    <section className="space-y-3" data-testid="join-policy-setting">
      <form onSubmit={save} className="space-y-3">
        <fieldset className="space-y-2">
          <legend>
            <Heading as="h2" level="card">
              {t("policyLegend")}
            </Heading>
          </legend>
          {option("open", t("policyOpen"), t("policyOpenHint"))}
          {option("approved_only", t("policyApprovedOnly"), t("policyApprovedOnlyHint"))}
        </fieldset>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="submit"
            disabled={busy || choice === settings.policy}
            data-testid="join-policy-save"
          >
            {t("policySave")}
          </Button>
          <Text tone="muted" data-testid="join-policy-counts">
            {t("counts", { approved: settings.approvedCount, students: settings.studentCount })}
          </Text>
        </div>
      </form>
      {notice ? (
        <p role="status" className="text-sm" data-testid="join-policy-notice">
          {notice}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-[var(--danger)]" data-testid="join-policy-error">
          {error}
        </p>
      ) : null}
    </section>
  );
}
